import type { Dispatch } from 'redux';
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import type { MarketingLead, MarketUpdate, Subscription, UnsubscribeParams } from 'app/shared/types/Marketing';

import requester, { createRequestHeaders } from '@zg-rentals/particles-js-requester';

import AppActions from 'app/shared/flux/actions/AppActions';
import StringConstants from 'app/shared/constants/StringConstants';
import { logError } from '@zg-rentals/log-error';
import { getGlobalLogger } from '@zg-rentals/logger-base';

export const rentalManagerClient = requester({
  proxyPath: __DEV__ ? StringConstants.PROXY_PATH : undefined,
  api: typeof window === 'undefined' ? process.env.ZG_API : window.location.origin,
  logger: getGlobalLogger('requester'),
});

const rentalGrowthClient = requester({
  proxyPath: StringConstants.RG_PROXY_PATH,
  api: typeof window === 'undefined' ? process.env.RG_API : '',
  logger: getGlobalLogger('growthRequester'),
  headers: { 'Content-Type': 'text/plain' },
});

const parseLogger = getGlobalLogger('parseApiResponse');

const wrapDispatch = <ResponseType>(
  request: AxiosInstance['get' | 'post' | 'put' | 'delete'],
  endpoint: string,
  config: AxiosRequestConfig = {},
) => {
  // redux state hasn't been converted to ts yet
  return async (dispatch: Dispatch, getState: () => any): Promise<ResponseType> => {
    const state = getState();
    const requestConfig = {
      ...config,
      headers: {
        ...createRequestHeaders({
          cookies: state.app.originalServerSideRequestCookies,
          ip: state.user.ip,
          userAgent: state.app.userAgent,
          brand: StringConstants.ZILLOW_BRAND,
        }),
        'x-csrf-token': state.app.csrfToken,
        ...(__BROWSER__ && { Accept: 'application/json' }),
      },
    };

    parseLogger?.info(requestConfig, 'request config');

    let response: AxiosResponse<any, any>;
    try {
      response = await request(
        endpoint,
        config.data ? config.data : requestConfig,
        config.data ? requestConfig : undefined,
      );
    } catch (error) {
      logError({
        error,
        errorType: 'HTTPError',
        context: {
          details: 'Error making api request',
          endpoint,
          root: 'utils/api#wrapDispatch',
          userAgent: state?.app?.userAgent,
        },
      });
      throw error;
    }

    parseLogger?.info(
      {
        status: response.status,
        statusText: response.statusText,
        data: response.data,
      },
      'received api response',
    );

    if (response.headers['set-cookie']) {
      dispatch(
        AppActions.updateApp({
          apiResponseServerSideRequestCookies: response.headers['set-cookie'],
        }),
      );
    }

    if (response.data.response) {
      return response.data.response;
    }

    return response.data;
  };
};

const api = {
  address: {
    autocomplete(partialAddress: string) {
      return wrapDispatch(
        rentalManagerClient.get,
        `${StringConstants.RENTAL_MANAGER_API_V1_PREFIX}/properties/addresses/autocomplete`,
        {
          params: {
            partialAddress,
          },
          timeout: 5000,
        },
      );
    },
    getListingForAddress(fullAddress: string, unit: string) {
      return wrapDispatch(
        rentalManagerClient.get,
        `${StringConstants.RENTAL_MANAGER_API_V1_PREFIX}/users/properties/listing/getListingForAddress`,
        {
          params: {
            fullAddress,
            unit,
          },
          timeout: 5000,
        },
      );
    },
    graphQlListing(data: string) {
      return wrapDispatch(rentalGrowthClient.post, 'rental-growth-api/api/graphql/v1/listing', {
        data,
      });
    },
  },
  area: {
    autocomplete(partial: string, types?: Array<string>) {
      return wrapDispatch(rentalGrowthClient.get, 'rental-growth-api/api/v1/area/autoComplete', {
        params: {
          partial,
          ...(types && { types }),
        },
        timeout: 5000,
      });
    },
    graphQlMarketTrends(data: string) {
      return wrapDispatch(rentalGrowthClient.post, 'rental-growth-api/api/graphql/v1/marketPages', {
        data,
      });
    },
  },
  marketing: {
    createMarketingLead(params: MarketingLead) {
      return wrapDispatch(rentalGrowthClient.get, 'rental-growth-api/api/v1/marketing/marketingLead/create', {
        params,
      });
    },
    createMarketUpdate(data: MarketUpdate) {
      return wrapDispatch(rentalGrowthClient.post, 'rental-growth-api/api/v1/marketing/marketUpdate/create', {
        data: JSON.stringify(data),
      });
    },
    updateSubscription(data: Subscription) {
      return wrapDispatch(rentalGrowthClient.post, 'rental-growth-api/api/v1/marketing/marketUpdate/updateStatus', {
        data: JSON.stringify(data),
      });
    },
    unsubscribeAll(data: UnsubscribeParams) {
      return wrapDispatch(rentalGrowthClient.post, 'rental-growth-api/api/v1/marketing/marketUpdate/unsubscribe', {
        data: JSON.stringify(data),
      });
    },
  },
  multibrandSupport: {
    getAppConfigFeature(appName = 'zrm_web', featureName: string) {
      return wrapDispatch(
        rentalManagerClient.get,
        `${StringConstants.RENTAL_MANAGER_API_V1_PREFIX}/multibrandSupport/appConfig/getFeature`,
        {
          params: { appName, featureName },
        },
      );
    },
  },
  user: {
    info: (queryParams: unknown) =>
      wrapDispatch(rentalManagerClient.get, 'rental-manager-api/api/web/v2/users/get', {
        params: queryParams,
      }),
  },
};

export default api;
